import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    UserPlus, BookOpen, Wallet, Mail, CheckCircle2, ChevronRight,
    ChevronLeft, Loader2, Copy, Sparkles, ArrowRight, Clock, GraduationCap,
    Calendar as CalendarIcon, IdCard, Globe2,
} from "lucide-react";
import { toast } from "sonner";
import { upcomingIntakes, computeEndDate } from "@/lib/intake-dates";

// Curated dial-code list (ISO + country + dial code). The catalogue is
// intentionally small but covers every market the college actively recruits
// from. Sorted by country name for predictable scanning.
const DIAL_CODES = [
    { iso: "CA", name: "Canada", dial: "+1", flag: "🇨🇦" },
    { iso: "US", name: "United States", dial: "+1", flag: "🇺🇸" },
    { iso: "IN", name: "India", dial: "+91", flag: "🇮🇳" },
    { iso: "NG", name: "Nigeria", dial: "+234", flag: "🇳🇬" },
    { iso: "GB", name: "United Kingdom", dial: "+44", flag: "🇬🇧" },
    { iso: "AU", name: "Australia", dial: "+61", flag: "🇦🇺" },
    { iso: "PH", name: "Philippines", dial: "+63", flag: "🇵🇭" },
    { iso: "PK", name: "Pakistan", dial: "+92", flag: "🇵🇰" },
    { iso: "BD", name: "Bangladesh", dial: "+880", flag: "🇧🇩" },
    { iso: "LK", name: "Sri Lanka", dial: "+94", flag: "🇱🇰" },
    { iso: "KE", name: "Kenya", dial: "+254", flag: "🇰🇪" },
    { iso: "GH", name: "Ghana", dial: "+233", flag: "🇬🇭" },
    { iso: "ZA", name: "South Africa", dial: "+27", flag: "🇿🇦" },
    { iso: "AE", name: "UAE", dial: "+971", flag: "🇦🇪" },
    { iso: "DE", name: "Germany", dial: "+49", flag: "🇩🇪" },
    { iso: "FR", name: "France", dial: "+33", flag: "🇫🇷" },
    { iso: "BR", name: "Brazil", dial: "+55", flag: "🇧🇷" },
    { iso: "MX", name: "Mexico", dial: "+52", flag: "🇲🇽" },
    { iso: "CN", name: "China", dial: "+86", flag: "🇨🇳" },
    { iso: "JP", name: "Japan", dial: "+81", flag: "🇯🇵" },
    { iso: "SG", name: "Singapore", dial: "+65", flag: "🇸🇬" },
];
const DIAL_BY_ISO = Object.fromEntries(DIAL_CODES.map((c) => [c.iso, c]));

const BLANK = {
    email: "",
    name: "",
    first_name: "",
    last_name: "",
    gender: "",
    sin: "",
    visa_status: "",
    campus: "",
    intake_schedule: "",
    feed_in_course: "",
    admission_term: "",
    student_language: "English",
    phone_iso: "CA",
    phone_local: "",
    country: "",
    student_id: "",
    password: "",
    program_id: "",
    payment_mode: "paid_offline",
    residency: "domestic",
    notes: "",
    send_welcome_email: true,
    start_date: "",
    end_date: "",
    // LOA-related profile + flags
    dob: "",
    address_line1: "",
    address_city: "",
    address_province: "",
    address_postal_code: "",
    address_country: "Canada",
    generate_loa: false,
    email_loa: true,
    generate_col: false,
    email_col: true,
    credential_type: "certificate",
};

const PAYMENT_OPTIONS = [
    {
        key: "paid_offline",
        label: "Paid offline at the college",
        Icon: Wallet,
        hint: "Cash, debit, or e-transfer received in person",
        tone: "primary",
    },
    {
        key: "waived",
        label: "Waived / scholarship",
        Icon: GraduationCap,
        hint: "No charge — admin-granted access",
        tone: "accent",
    },
    {
        key: "online_stripe",
        label: "Marked as paid online",
        Icon: Sparkles,
        hint: "Stripe receipt already on file (manual mark)",
        tone: "muted",
    },
];

const STEPS = [
    { idx: 0, key: "details", label: "Student details" },
    { idx: 1, key: "program", label: "Pick a program" },
    { idx: 2, key: "payment", label: "Payment mode" },
    { idx: 3, key: "review", label: "Review & enrol" },
];

function Stepper({ active }) {
    return (
        <div className="flex items-center gap-2 mb-6" data-testid="enroll-wizard-stepper">
            {STEPS.map((s, i) => {
                const done = i < active;
                const current = i === active;
                return (
                    <React.Fragment key={s.key}>
                        <div
                            className="flex items-center gap-2"
                            data-testid={`enroll-wizard-step-${s.key}`}
                        >
                            <div
                                className={`flex items-center justify-center h-7 w-7 rounded-full text-xs font-semibold border ${
                                    current
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : done
                                          ? "bg-primary/10 text-primary border-primary/30"
                                          : "bg-muted text-muted-foreground border-border"
                                }`}
                            >
                                {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
                            </div>
                            <span
                                className={`text-xs font-medium ${
                                    current
                                        ? "text-foreground"
                                        : done
                                          ? "text-primary"
                                          : "text-muted-foreground"
                                }`}
                            >
                                {s.label}
                            </span>
                        </div>
                        {i < STEPS.length - 1 && (
                            <div className="flex-1 h-px bg-border" />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

export default function StudentEnrollmentWizard({ open, onOpenChange, programs = [], onDone, presetEmail, presetData }) {
    const [step, setStep] = useState(0);
    const [form, setForm] = useState(BLANK);
    const [existingUser, setExistingUser] = useState(undefined); // undefined=not checked, null=new, obj=hit
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (open) {
            setStep(0);
            // Pre-seed the email if the wizard was opened from a student's
            // dossier page — admin typically wants to enrol the same person
            // in another program, not type it again.
            // presetData takes precedence (used by the Pending Applications
            // "Approve student" flow which carries the entire applicant profile).
            const base = { ...BLANK, email: presetEmail || "" };
            const merged = presetData ? { ...base, ...presetData } : base;
            setForm(merged);
            setResult(null);
            setExistingUser(undefined);
        }
    }, [open, presetEmail, presetData]);

    // Email lookup (debounced)
    useEffect(() => {
        if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            setExistingUser(undefined);
            return undefined;
        }
        const t = window.setTimeout(async () => {
            try {
                const { data } = await api.get("/admin/users");
                const hit = (data || []).find(
                    (u) => u.email?.toLowerCase() === form.email.toLowerCase(),
                );
                if (hit) {
                    setExistingUser({ id: hit.id, email: hit.email, name: hit.name, role: hit.role });
                    setForm((f) => ({
                        ...f,
                        name: f.name || hit.name || "",
                        dob: f.dob || hit.dob || "",
                        address_line1: f.address_line1 || hit.address_line1 || "",
                        address_city: f.address_city || hit.address_city || "",
                        address_province: f.address_province || hit.address_province || "",
                        address_postal_code: f.address_postal_code || hit.address_postal_code || "",
                        address_country: f.address_country || hit.address_country || "Canada",
                    }));
                } else {
                    setExistingUser(null);
                }
            } catch {
                setExistingUser(undefined);
            }
        }, 350);
        return () => window.clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.email]);

    const selectedProgram = useMemo(
        () => programs.find((p) => p.id === form.program_id) || null,
        [programs, form.program_id],
    );

    // Keep end_date in sync with (program, start_date). Recomputes silently
    // whenever the user picks a different program or intake. If start_date
    // is blank we leave end_date blank too so validation catches it.
    useEffect(() => {
        const nextEnd = form.start_date
            ? computeEndDate(form.start_date, selectedProgram)
            : "";
        if (nextEnd !== form.end_date) {
            setForm((f) => ({ ...f, end_date: nextEnd }));
        }
    }, [form.start_date, form.end_date, selectedProgram]);

    const detailsValid = useMemo(() => {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return false;
        if (!existingUser && !form.name.trim()) return false;
        return true;
    }, [form.email, form.name, existingUser]);

    const goNext = () => {
        if (step === 0 && !detailsValid) {
            toast.error("Enter a valid email — and a name for new accounts.");
            return;
        }
        if (step === 1 && !form.program_id) {
            toast.error("Pick a program.");
            return;
        }
        setStep((s) => Math.min(STEPS.length - 1, s + 1));
    };
    const goBack = () => setStep((s) => Math.max(0, s - 1));

    const submit = async () => {
        setSaving(true);
        try {
            const dial = DIAL_BY_ISO[form.phone_iso]?.dial || "";
            const phoneLocal = form.phone_local.trim();
            const phoneCombined = phoneLocal
                ? `${dial} ${phoneLocal}`.trim()
                : "";
            const { data } = await api.post("/admin/enrollments/manual", {
                email: form.email.trim().toLowerCase(),
                name: form.name.trim() || undefined,
                password: form.password.trim() || undefined,
                phone: phoneCombined || undefined,
                country: form.country.trim() || undefined,
                student_id: form.student_id.trim() || undefined,
                program_id: form.program_id,
                payment_mode: form.payment_mode,
                residency: form.residency,
                notes: form.notes.trim() || undefined,
                send_welcome_email: form.send_welcome_email,
                start_date: form.start_date || undefined,
                end_date: form.end_date || undefined,
                dob: form.dob || undefined,
                address_line1: form.address_line1.trim() || undefined,
                address_city: form.address_city.trim() || undefined,
                address_province: form.address_province.trim() || undefined,
                address_postal_code: form.address_postal_code.trim() || undefined,
                address_country: form.address_country.trim() || undefined,
                generate_loa: !!form.generate_loa,
                email_loa: !!form.email_loa,
                generate_col: !!form.generate_col,
                email_col: !!form.email_col,
                credential_type: form.credential_type,
                // Extended PTIB profile
                first_name: form.first_name.trim() || undefined,
                last_name: form.last_name.trim() || undefined,
                gender: form.gender || undefined,
                sin: form.sin.trim() || undefined,
                visa_status: form.visa_status || undefined,
                campus: form.campus || undefined,
                intake_schedule: form.intake_schedule.trim() || undefined,
                feed_in_course: form.feed_in_course.trim() || undefined,
                admission_term: form.admission_term.trim() || undefined,
                student_language: form.student_language.trim() || undefined,
            });
            setResult(data);
            toast.success(
                data.user.created
                    ? "Student account created & enrolled"
                    : "Existing student enrolled in program",
            );
            if (data.loa?.error) {
                toast.warning(`LOA not issued: ${data.loa.error}`);
            } else if (data.loa?.emailed_to) {
                toast.success(`LOA ${data.loa.loa_number} emailed to ${data.loa.emailed_to}`);
            }
            if (data.col?.error) {
                toast.warning(`Conditional Offer Letter not issued: ${data.col.error}`);
            } else if (data.col?.emailed_to) {
                toast.success(`Conditional Offer ${data.col.col_number} emailed to ${data.col.emailed_to}`);
            }
            onDone?.(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not enrol student.");
        } finally {
            setSaving(false);
        }
    };

    const copy = (text) => {
        navigator.clipboard?.writeText(text);
        toast.success("Copied to clipboard");
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[92vh] overflow-y-auto"
                data-testid="enrollment-wizard-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2 text-2xl">
                        <GraduationCap className="h-6 w-6 text-primary" />
                        New Student Enrolment
                    </DialogTitle>
                    <DialogDescription>
                        Manually create or enrol a student — perfect for walk-ins, scholarships,
                        or admin-granted access.
                    </DialogDescription>
                </DialogHeader>

                {result ? (
                    <SuccessPanel
                        result={result}
                        onCopy={copy}
                        onClose={() => onOpenChange(false)}
                        setResult={setResult}
                    />
                ) : (
                    <>
                        <Stepper active={step} />

                        {/* STEP 1: Details */}
                        {step === 0 && (
                            <div className="space-y-4 anim-fade-up">
                                <div>
                                    <Label className="text-sm font-semibold">Student email</Label>
                                    <Input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) =>
                                            setForm({ ...form, email: e.target.value })
                                        }
                                        placeholder="student@example.com"
                                        autoFocus
                                        data-testid="wizard-email-input"
                                    />
                                    {existingUser && existingUser.role === "student" ? (
                                        <p
                                            className="text-xs text-primary mt-1.5 flex items-center gap-1.5"
                                            data-testid="wizard-existing-student-hint"
                                        >
                                            <CheckCircle2 className="h-3.5 w-3.5" />
                                            Existing student account: <strong>{existingUser.name}</strong>
                                        </p>
                                    ) : existingUser && existingUser.role !== "student" ? (
                                        <p
                                            className="text-xs text-destructive mt-1.5"
                                            data-testid="wizard-existing-nonstudent-warning"
                                        >
                                            This email belongs to a {existingUser.role}. Pick a different email.
                                        </p>
                                    ) : existingUser === null ? (
                                        <p
                                            className="text-xs text-muted-foreground mt-1.5"
                                            data-testid="wizard-new-student-hint"
                                        >
                                            No account yet — we&rsquo;ll create one.
                                        </p>
                                    ) : null}
                                </div>

                                {(existingUser === null || existingUser === undefined) && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="sm:col-span-2">
                                            <Label className="text-sm font-semibold">Full name</Label>
                                            <Input
                                                value={form.name}
                                                onChange={(e) =>
                                                    setForm({ ...form, name: e.target.value })
                                                }
                                                placeholder="Jasmine Patel"
                                                data-testid="wizard-name-input"
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <Label className="text-sm font-semibold">
                                                Phone <span className="font-normal text-muted-foreground">(optional)</span>
                                            </Label>
                                            <div className="flex gap-2">
                                                <Select
                                                    value={form.phone_iso}
                                                    onValueChange={(v) =>
                                                        setForm({ ...form, phone_iso: v })
                                                    }
                                                >
                                                    <SelectTrigger
                                                        className="w-[140px] flex-shrink-0"
                                                        data-testid="wizard-phone-iso-trigger"
                                                    >
                                                        <SelectValue>
                                                            {form.phone_iso ? (
                                                                <span className="flex items-center gap-1.5">
                                                                    <span className="text-base leading-none">
                                                                        {DIAL_BY_ISO[form.phone_iso]?.flag}
                                                                    </span>
                                                                    <span className="font-mono text-xs">
                                                                        {DIAL_BY_ISO[form.phone_iso]?.dial}
                                                                    </span>
                                                                </span>
                                                            ) : null}
                                                        </SelectValue>
                                                    </SelectTrigger>
                                                    <SelectContent className="max-h-72">
                                                        {DIAL_CODES.map((c) => (
                                                            <SelectItem
                                                                key={c.iso}
                                                                value={c.iso}
                                                                data-testid={`wizard-phone-iso-${c.iso}`}
                                                            >
                                                                <span className="flex items-center gap-2">
                                                                    <span className="text-base leading-none">
                                                                        {c.flag}
                                                                    </span>
                                                                    <span className="text-sm flex-1">
                                                                        {c.name}
                                                                    </span>
                                                                    <span className="font-mono text-xs text-muted-foreground">
                                                                        {c.dial}
                                                                    </span>
                                                                </span>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <Input
                                                    value={form.phone_local}
                                                    onChange={(e) =>
                                                        setForm({ ...form, phone_local: e.target.value })
                                                    }
                                                    placeholder="604 555 0123"
                                                    inputMode="tel"
                                                    data-testid="wizard-phone-input"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <Label className="text-sm font-semibold">
                                                Country{" "}
                                                <span className="font-normal text-muted-foreground">(optional)</span>
                                            </Label>
                                            <Select
                                                value={form.country || "__none"}
                                                onValueChange={(v) =>
                                                    setForm({
                                                        ...form,
                                                        country: v === "__none" ? "" : v,
                                                    })
                                                }
                                            >
                                                <SelectTrigger data-testid="wizard-country-trigger">
                                                    <SelectValue placeholder="Select country" />
                                                </SelectTrigger>
                                                <SelectContent className="max-h-72">
                                                    <SelectItem value="__none">— Not set —</SelectItem>
                                                    {DIAL_CODES.map((c) => (
                                                        <SelectItem
                                                            key={c.iso}
                                                            value={c.name}
                                                            data-testid={`wizard-country-${c.iso}`}
                                                        >
                                                            <span className="flex items-center gap-2">
                                                                <span className="text-base leading-none">
                                                                    {c.flag}
                                                                </span>
                                                                <span className="text-sm">{c.name}</span>
                                                            </span>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-sm font-semibold flex items-center gap-1.5">
                                                <IdCard className="h-3.5 w-3.5 text-muted-foreground" />
                                                Student ID{" "}
                                                <span className="font-normal text-muted-foreground">(optional)</span>
                                            </Label>
                                            <Input
                                                value={form.student_id}
                                                onChange={(e) =>
                                                    setForm({ ...form, student_id: e.target.value })
                                                }
                                                placeholder="HCC-2026-0042 (auto if blank)"
                                                className="font-mono"
                                                data-testid="wizard-student-id-input"
                                            />
                                            <p className="text-[11px] text-muted-foreground mt-1">
                                                Appears on certificate &amp; profile. Must be
                                                unique. Leave blank to auto-assign.
                                            </p>
                                        </div>
                                        <div className="sm:col-span-2">
                                            <Label className="text-sm font-semibold">
                                                Temporary password{" "}
                                                <span className="font-normal text-muted-foreground">
                                                    (auto-generated if blank)
                                                </span>
                                            </Label>
                                            <Input
                                                value={form.password}
                                                onChange={(e) =>
                                                    setForm({ ...form, password: e.target.value })
                                                }
                                                placeholder="Leave blank for a strong auto-password"
                                                data-testid="wizard-password-input"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* DOB + mailing address — required if the admin
                                    plans to issue a PTIB-compliant Letter of
                                    Acceptance. Always shown so the admin can
                                    confirm/edit values pulled from an existing
                                    account too. */}
                                <div className="rounded-lg border border-amber-500/30 bg-amber-50/40 p-3 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-[11px] uppercase tracking-[0.18em] text-amber-800 font-semibold m-0">
                                            Profile for Letter of Acceptance
                                        </Label>
                                        <span className="text-[10px] text-muted-foreground">
                                            Required for LOA · Optional otherwise
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-xs text-muted-foreground">
                                                Date of birth
                                            </Label>
                                            <Input
                                                type="date"
                                                value={form.dob}
                                                onChange={(e) => setForm({ ...form, dob: e.target.value })}
                                                data-testid="wizard-dob-input"
                                                className="bg-background"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-muted-foreground">
                                                Country (mailing)
                                            </Label>
                                            <Input
                                                value={form.address_country}
                                                onChange={(e) =>
                                                    setForm({ ...form, address_country: e.target.value })
                                                }
                                                placeholder="Canada"
                                                data-testid="wizard-address-country-input"
                                                className="bg-background"
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <Label className="text-xs text-muted-foreground">
                                                Mailing address
                                            </Label>
                                            <Input
                                                value={form.address_line1}
                                                onChange={(e) =>
                                                    setForm({ ...form, address_line1: e.target.value })
                                                }
                                                placeholder="123 Heritage Way"
                                                data-testid="wizard-address-line1-input"
                                                className="bg-background"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-muted-foreground">City</Label>
                                            <Input
                                                value={form.address_city}
                                                onChange={(e) =>
                                                    setForm({ ...form, address_city: e.target.value })
                                                }
                                                placeholder="Surrey"
                                                data-testid="wizard-address-city-input"
                                                className="bg-background"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-muted-foreground">
                                                Province / State
                                            </Label>
                                            <Input
                                                value={form.address_province}
                                                onChange={(e) =>
                                                    setForm({ ...form, address_province: e.target.value })
                                                }
                                                placeholder="BC"
                                                data-testid="wizard-address-province-input"
                                                className="bg-background"
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <Label className="text-xs text-muted-foreground">
                                                Postal / ZIP code
                                            </Label>
                                            <Input
                                                value={form.address_postal_code}
                                                onChange={(e) =>
                                                    setForm({ ...form, address_postal_code: e.target.value })
                                                }
                                                placeholder="V3T 0A1"
                                                data-testid="wizard-address-postal-input"
                                                className="bg-background"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Extended PTIB profile fields — gender, SIN
                                    (encrypted at rest), visa status, campus,
                                    schedule, feed-in course, admission term,
                                    instructional language. */}
                                <div
                                    className="rounded-lg border border-primary/25 bg-primary/[0.04] p-3 space-y-3"
                                    data-testid="wizard-ptib-section"
                                >
                                    <div className="flex items-center justify-between">
                                        <Label className="text-[11px] uppercase tracking-[0.18em] text-primary font-semibold m-0">
                                            PTIB student profile (regulated)
                                        </Label>
                                        <span className="text-[10px] text-muted-foreground">
                                            SIN encrypted at rest
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-xs text-muted-foreground">
                                                Gender
                                            </Label>
                                            <Select
                                                value={form.gender || "__none"}
                                                onValueChange={(v) =>
                                                    setForm({ ...form, gender: v === "__none" ? "" : v })
                                                }
                                            >
                                                <SelectTrigger
                                                    className="bg-background"
                                                    data-testid="wizard-gender-trigger"
                                                >
                                                    <SelectValue placeholder="Select" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="__none">— Not set —</SelectItem>
                                                    <SelectItem value="male" data-testid="wizard-gender-male">Male</SelectItem>
                                                    <SelectItem value="female" data-testid="wizard-gender-female">Female</SelectItem>
                                                    <SelectItem value="non_binary" data-testid="wizard-gender-non_binary">Non-binary</SelectItem>
                                                    <SelectItem value="prefer_not_to_say" data-testid="wizard-gender-prefer_not_to_say">
                                                        Prefer not to say
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                                                Canadian SIN
                                                <span className="text-[10px] text-primary/80">(encrypted)</span>
                                            </Label>
                                            <Input
                                                value={form.sin}
                                                onChange={(e) => {
                                                    const digits = e.target.value.replace(/\D/g, "").slice(0, 9);
                                                    const grouped = digits
                                                        .replace(/^(\d{3})(\d{0,3})(\d{0,3})$/, (m, a, b, c) =>
                                                            [a, b, c].filter(Boolean).join("-"),
                                                        );
                                                    setForm({ ...form, sin: grouped });
                                                }}
                                                placeholder="123-456-789"
                                                inputMode="numeric"
                                                autoComplete="off"
                                                className="bg-background font-mono"
                                                data-testid="wizard-sin-input"
                                            />
                                            <p className="text-[10px] text-muted-foreground mt-1">
                                                Stored encrypted. Only the last 3 digits are shown later.
                                            </p>
                                        </div>

                                        <div>
                                            <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                                                <Globe2 className="h-3 w-3" />
                                                Visa / residency status
                                            </Label>
                                            <Select
                                                value={form.visa_status || "__none"}
                                                onValueChange={(v) =>
                                                    setForm({ ...form, visa_status: v === "__none" ? "" : v })
                                                }
                                            >
                                                <SelectTrigger
                                                    className="bg-background"
                                                    data-testid="wizard-visa-trigger"
                                                >
                                                    <SelectValue placeholder="Select" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="__none">— Not set —</SelectItem>
                                                    <SelectItem value="citizen" data-testid="wizard-visa-citizen">Canadian citizen</SelectItem>
                                                    <SelectItem value="permanent_resident" data-testid="wizard-visa-pr">Permanent resident</SelectItem>
                                                    <SelectItem value="study_permit" data-testid="wizard-visa-study">Study permit</SelectItem>
                                                    <SelectItem value="work_permit" data-testid="wizard-visa-work">Work permit</SelectItem>
                                                    <SelectItem value="other" data-testid="wizard-visa-other">Other</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-xs text-muted-foreground">
                                                Campus
                                            </Label>
                                            <Select
                                                value={form.campus || "__none"}
                                                onValueChange={(v) =>
                                                    setForm({ ...form, campus: v === "__none" ? "" : v })
                                                }
                                            >
                                                <SelectTrigger
                                                    className="bg-background"
                                                    data-testid="wizard-campus-trigger"
                                                >
                                                    <SelectValue placeholder="Select" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="__none">— Not set —</SelectItem>
                                                    <SelectItem value="Surrey">Surrey, BC</SelectItem>
                                                    <SelectItem value="Online">Online</SelectItem>
                                                    <SelectItem value="Hybrid">Hybrid (Surrey + Online)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div>
                                            <Label className="text-xs text-muted-foreground">
                                                Intake schedule
                                            </Label>
                                            <Input
                                                value={form.intake_schedule}
                                                onChange={(e) =>
                                                    setForm({ ...form, intake_schedule: e.target.value })
                                                }
                                                placeholder="Mon–Fri · 9 AM–1 PM"
                                                className="bg-background"
                                                data-testid="wizard-intake-schedule-input"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-muted-foreground">
                                                Admission term
                                            </Label>
                                            <Input
                                                value={form.admission_term}
                                                onChange={(e) =>
                                                    setForm({ ...form, admission_term: e.target.value })
                                                }
                                                placeholder="Spring 2026"
                                                className="bg-background"
                                                data-testid="wizard-admission-term-input"
                                            />
                                        </div>

                                        <div className="col-span-2">
                                            <Label className="text-xs text-muted-foreground">
                                                Feed-in course / prerequisite path
                                            </Label>
                                            <Input
                                                value={form.feed_in_course}
                                                onChange={(e) =>
                                                    setForm({ ...form, feed_in_course: e.target.value })
                                                }
                                                placeholder="e.g. Business Foundations — Term A"
                                                className="bg-background"
                                                data-testid="wizard-feed-in-course-input"
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <Label className="text-xs text-muted-foreground">
                                                Student language of instruction
                                            </Label>
                                            <Input
                                                value={form.student_language}
                                                onChange={(e) =>
                                                    setForm({ ...form, student_language: e.target.value })
                                                }
                                                placeholder="English"
                                                className="bg-background"
                                                data-testid="wizard-student-language-input"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 2: Program */}
                        {step === 1 && (
                            <div className="space-y-3 anim-fade-up" data-testid="wizard-program-list">
                                <Label className="text-sm font-semibold">Choose a program</Label>
                                <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                                    {programs.length === 0 ? (
                                        <p className="text-sm text-muted-foreground py-6 text-center">
                                            No programs available yet.
                                        </p>
                                    ) : (
                                        programs.map((p) => {
                                            const selected = form.program_id === p.id;
                                            return (
                                                <button
                                                    key={p.id}
                                                    type="button"
                                                    onClick={() =>
                                                        setForm({ ...form, program_id: p.id })
                                                    }
                                                    className={`w-full text-left rounded-lg border p-4 transition-all ${
                                                        selected
                                                            ? "border-primary bg-primary/5 ring-2 ring-primary/30"
                                                            : "border-border bg-card hover:border-primary/50 hover:bg-muted/50"
                                                    }`}
                                                    data-testid={`wizard-program-option-${p.id}`}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <div
                                                            className={`mt-0.5 h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                                                                selected
                                                                    ? "border-primary bg-primary"
                                                                    : "border-border bg-background"
                                                            }`}
                                                        >
                                                            {selected && (
                                                                <CheckCircle2 className="h-4 w-4 text-primary-foreground" />
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="font-display text-base text-foreground">
                                                                    {p.title}
                                                                </span>
                                                                {p.is_published ? (
                                                                    <Badge
                                                                        variant="outline"
                                                                        className="text-[10px] border-primary/30 text-primary"
                                                                    >
                                                                        Published
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge
                                                                        variant="outline"
                                                                        className="text-[10px]"
                                                                    >
                                                                        Draft
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                                                                {typeof p.price === "number" && (
                                                                    <span className="inline-flex items-center gap-1">
                                                                        <Wallet className="h-3 w-3" />$
                                                                        {Number(p.price).toFixed(2)}
                                                                        {p.price_international
                                                                            && Number(p.price_international) > 0 ? (
                                                                            <span className="text-muted-foreground">
                                                                                {" / $"}
                                                                                {Number(p.price_international).toFixed(2)}
                                                                                <span className="ml-0.5 text-[10px] uppercase tracking-[0.14em]">
                                                                                    intl
                                                                                </span>
                                                                            </span>
                                                                        ) : null}
                                                                    </span>
                                                                )}
                                                                {p.total_hours ? (
                                                                    <span className="inline-flex items-center gap-1">
                                                                        <Clock className="h-3 w-3" />
                                                                        {p.total_hours} hrs
                                                                    </span>
                                                                ) : null}
                                                                {p.total_chapters ? (
                                                                    <span className="inline-flex items-center gap-1">
                                                                        <BookOpen className="h-3 w-3" />
                                                                        {p.total_chapters} chapters
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>

                                {/* Credential type — drives the LOA "Level of study" */}
                                {form.program_id ? (
                                    <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3 anim-fade-up">
                                        <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold m-0">
                                            Credential type
                                        </Label>
                                        <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">
                                            Determines the &ldquo;Level of study&rdquo; on the Letter of Acceptance.
                                        </p>
                                        <div className="grid grid-cols-2 gap-2">
                                            {[
                                                { key: "certificate", label: "Certificate" },
                                                { key: "diploma", label: "Diploma" },
                                            ].map((opt) => {
                                                const selected = form.credential_type === opt.key;
                                                return (
                                                    <button
                                                        key={opt.key}
                                                        type="button"
                                                        onClick={() =>
                                                            setForm({ ...form, credential_type: opt.key })
                                                        }
                                                        className={`rounded-md border p-2.5 text-sm font-medium transition-all ${
                                                            selected
                                                                ? "border-primary bg-primary/10 text-primary ring-2 ring-primary/30"
                                                                : "border-border bg-background hover:border-primary/50"
                                                        }`}
                                                        data-testid={`wizard-credential-type-${opt.key}`}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        )}

                        {/* STEP 3: Payment mode */}
                        {step === 2 && (
                            <div className="space-y-3 anim-fade-up">
                                <Label className="text-sm font-semibold">How is the student paying?</Label>
                                <div className="space-y-2">
                                    {PAYMENT_OPTIONS.map((opt) => {
                                        const selected = form.payment_mode === opt.key;
                                        return (
                                            <button
                                                key={opt.key}
                                                type="button"
                                                onClick={() =>
                                                    setForm({ ...form, payment_mode: opt.key })
                                                }
                                                className={`w-full text-left rounded-lg border p-4 transition-all flex items-start gap-3 ${
                                                    selected
                                                        ? "border-primary bg-primary/5 ring-2 ring-primary/30"
                                                        : "border-border bg-card hover:border-primary/50 hover:bg-muted/50"
                                                }`}
                                                data-testid={`wizard-payment-option-${opt.key}`}
                                            >
                                                <div
                                                    className={`mt-0.5 h-9 w-9 rounded-md flex items-center justify-center ${
                                                        selected
                                                            ? "bg-primary text-primary-foreground"
                                                            : "bg-muted text-muted-foreground"
                                                    }`}
                                                >
                                                    <opt.Icon className="h-4 w-4" />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm">{opt.label}</div>
                                                    <div className="text-xs text-muted-foreground mt-0.5">
                                                        {opt.hint}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="pt-2">
                                    <Label className="text-sm font-semibold">
                                        Residency — drives which tuition tier is booked
                                    </Label>
                                    <div
                                        className="mt-2 grid grid-cols-2 gap-2"
                                        data-testid="wizard-residency-group"
                                    >
                                        {[
                                            { key: "domestic", label: "Domestic" },
                                            { key: "international", label: "International" },
                                        ].map((opt) => {
                                            const on = form.residency === opt.key;
                                            const price =
                                                opt.key === "international"
                                                    ? selectedProgram?.price_international
                                                    : selectedProgram?.price;
                                            return (
                                                <button
                                                    key={opt.key}
                                                    type="button"
                                                    onClick={() =>
                                                        setForm({ ...form, residency: opt.key })
                                                    }
                                                    className={`rounded-lg border p-3 text-left transition-colors ${
                                                        on
                                                            ? "border-primary bg-primary/10"
                                                            : "border-border hover:border-primary/50"
                                                    }`}
                                                    data-testid={`wizard-residency-${opt.key}`}
                                                >
                                                    <div className="text-sm font-semibold">
                                                        {opt.label}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                        {price != null && price !== ""
                                                            ? `$${Number(price).toFixed(2)} CAD`
                                                            : opt.key === "international"
                                                                ? "Uses domestic fee"
                                                                : "—"}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="pt-2">
                                    <Label className="text-sm font-semibold">Notes (optional)</Label>
                                    <Textarea
                                        rows={2}
                                        value={form.notes}
                                        onChange={(e) =>
                                            setForm({ ...form, notes: e.target.value })
                                        }
                                        placeholder='e.g. "Paid $299 cash — receipt #A-1042"'
                                        data-testid="wizard-notes-input"
                                    />
                                </div>
                                <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-sm font-semibold flex items-center gap-1.5">
                                            <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                                            Intake start
                                        </Label>
                                        <Select
                                            value={form.start_date || ""}
                                            onValueChange={(v) => {
                                                const end = computeEndDate(v, selectedProgram);
                                                setForm({ ...form, start_date: v, end_date: end });
                                            }}
                                        >
                                            <SelectTrigger data-testid="wizard-start-date-select">
                                                <SelectValue placeholder="Select an intake…" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {upcomingIntakes(6).map((it) => (
                                                    <SelectItem key={it.iso} value={it.iso}>
                                                        {it.label}
                                                        <span className="ml-2 text-[10px] text-muted-foreground">
                                                            ({it.which})
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-[11px] text-muted-foreground mt-1">
                                            Intakes run on the 1st and 3rd Monday of every month.
                                        </p>
                                    </div>
                                    <div>
                                        <Label className="text-sm font-semibold flex items-center gap-1.5">
                                            <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                                            End date
                                            <span className="font-normal text-muted-foreground">(auto)</span>
                                        </Label>
                                        <Input
                                            type="date"
                                            value={form.end_date}
                                            readOnly
                                            className="bg-muted/40 cursor-not-allowed"
                                            data-testid="wizard-end-date-input"
                                        />
                                        <p className="text-[11px] text-muted-foreground mt-1">
                                            Computed from the program duration
                                            {selectedProgram ? (
                                                <>
                                                    {" "}(
                                                    {Number(selectedProgram.intended_duration_days)
                                                        || (selectedProgram.credential_type === "diploma" ? 240 : 90)}
                                                    {" "}days).
                                                </>
                                            ) : "."}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 4: Review */}
                        {step === 3 && (
                            <div className="space-y-4 anim-fade-up" data-testid="wizard-review-panel">
                                <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                                    <ReviewRow
                                        Icon={UserPlus}
                                        label="Student"
                                        value={
                                            existingUser
                                                ? `${existingUser.name} · existing account`
                                                : `${form.name || "—"} · new account`
                                        }
                                        sub={form.email}
                                    />
                                    <ReviewRow
                                        Icon={BookOpen}
                                        label="Program"
                                        value={selectedProgram?.title || "—"}
                                        sub={(() => {
                                            if (!selectedProgram) return "";
                                            const hasIntl =
                                                selectedProgram.price_international != null
                                                && Number(selectedProgram.price_international) > 0;
                                            const amount =
                                                form.residency === "international" && hasIntl
                                                    ? Number(selectedProgram.price_international)
                                                    : Number(selectedProgram.price || 0);
                                            return `$${amount.toFixed(2)} · ${form.residency} · ${form.credential_type === "diploma" ? "Diploma" : "Certificate"}`;
                                        })()}
                                    />
                                    <ReviewRow
                                        Icon={Wallet}
                                        label="Payment"
                                        value={
                                            PAYMENT_OPTIONS.find(
                                                (o) => o.key === form.payment_mode,
                                            )?.label || form.payment_mode
                                        }
                                        sub={form.notes}
                                    />
                                    {(form.start_date || form.end_date) ? (
                                        <ReviewRow
                                            Icon={CalendarIcon}
                                            label="Access window"
                                            value={
                                                form.start_date && form.end_date
                                                    ? `${form.start_date} → ${form.end_date}`
                                                    : form.start_date
                                                        ? `From ${form.start_date}`
                                                        : `Until ${form.end_date}`
                                            }
                                        />
                                    ) : null}
                                    {(form.student_id || form.country) ? (
                                        <ReviewRow
                                            Icon={IdCard}
                                            label="Student ID"
                                            value={form.student_id || "Auto-generate"}
                                            sub={form.country || undefined}
                                        />
                                    ) : null}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="wizard-welcome-email"
                                        checked={form.send_welcome_email}
                                        onCheckedChange={(v) =>
                                            setForm({ ...form, send_welcome_email: !!v })
                                        }
                                        data-testid="wizard-send-welcome-checkbox"
                                    />
                                    <Label
                                        htmlFor="wizard-welcome-email"
                                        className="cursor-pointer text-sm font-normal flex items-center gap-1.5"
                                    >
                                        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                                        Email the student their login + welcome message
                                    </Label>
                                </div>

                                {/* LOA controls — only meaningful for the
                                    domestic / paid_offline path but offered to
                                    every walk-in enrolment. Backend blocks
                                    generation when DOB / address / start_date
                                    are missing and surfaces a non-fatal error. */}
                                <div className="rounded-lg border border-amber-500/30 bg-amber-50/40 p-3 space-y-2">
                                    <div className="flex items-start gap-2">
                                        <Checkbox
                                            id="wizard-gen-loa"
                                            checked={form.generate_loa}
                                            onCheckedChange={(v) =>
                                                setForm({ ...form, generate_loa: !!v })
                                            }
                                            data-testid="wizard-generate-loa-checkbox"
                                        />
                                        <div className="flex-1">
                                            <Label
                                                htmlFor="wizard-gen-loa"
                                                className="cursor-pointer text-sm font-medium flex items-center gap-1.5"
                                            >
                                                <IdCard className="h-3.5 w-3.5 text-amber-700" />
                                                Issue a Letter of Acceptance (PTIB-compliant PDF)
                                            </Label>
                                            {form.generate_loa && (!form.dob || !form.address_line1.trim() || !form.start_date || !form.end_date) ? (
                                                <p
                                                    className="text-xs text-amber-800 mt-1.5"
                                                    data-testid="wizard-loa-missing-warning"
                                                >
                                                    Heads up: DOB, mailing address, program start date, and program end date are
                                                    required. The enrolment will still succeed, but the LOA
                                                    won&rsquo;t be generated until those are filled in.
                                                </p>
                                            ) : null}
                                        </div>
                                    </div>
                                    {form.generate_loa ? (
                                        <div className="flex items-center gap-2 pl-6">
                                            <Checkbox
                                                id="wizard-email-loa"
                                                checked={form.email_loa}
                                                onCheckedChange={(v) =>
                                                    setForm({ ...form, email_loa: !!v })
                                                }
                                                data-testid="wizard-email-loa-checkbox"
                                            />
                                            <Label
                                                htmlFor="wizard-email-loa"
                                                className="cursor-pointer text-xs font-normal text-muted-foreground"
                                            >
                                                Also email the LOA PDF to the student as an attachment
                                            </Label>
                                        </div>
                                    ) : null}
                                </div>

                                {/* Conditional Offer Letter (COL) — for
                                    students who have not yet paid tuition. The
                                    COL can later be upgraded to a full LOA
                                    from the Students table once fees clear. */}
                                <div
                                    className="rounded-lg border border-primary/30 bg-primary/[0.05] p-3 space-y-2"
                                    data-testid="wizard-col-section"
                                >
                                    <div className="flex items-start gap-2">
                                        <Checkbox
                                            id="wizard-gen-col"
                                            checked={form.generate_col}
                                            onCheckedChange={(v) =>
                                                setForm({ ...form, generate_col: !!v })
                                            }
                                            data-testid="wizard-generate-col-checkbox"
                                        />
                                        <div className="flex-1">
                                            <Label
                                                htmlFor="wizard-gen-col"
                                                className="cursor-pointer text-sm font-medium flex items-center gap-1.5"
                                            >
                                                <IdCard className="h-3.5 w-3.5 text-primary" />
                                                Issue a Conditional Offer Letter (fees not yet paid)
                                            </Label>
                                            <p className="text-[11px] text-muted-foreground mt-0.5">
                                                Use this when the student hasn&rsquo;t paid tuition yet.
                                                You can upgrade it to a full LOA later from the Students table.
                                            </p>
                                            {form.generate_col && (!form.dob || !form.address_line1.trim() || !form.start_date || !form.end_date) ? (
                                                <p
                                                    className="text-xs text-amber-800 mt-1.5"
                                                    data-testid="wizard-col-missing-warning"
                                                >
                                                    Heads up: DOB, mailing address, program start &amp; end dates are
                                                    required. The enrolment will still succeed, but the COL won&rsquo;t
                                                    be generated until those are filled in.
                                                </p>
                                            ) : null}
                                        </div>
                                    </div>
                                    {form.generate_col ? (
                                        <div className="flex items-center gap-2 pl-6">
                                            <Checkbox
                                                id="wizard-email-col"
                                                checked={form.email_col}
                                                onCheckedChange={(v) =>
                                                    setForm({ ...form, email_col: !!v })
                                                }
                                                data-testid="wizard-email-col-checkbox"
                                            />
                                            <Label
                                                htmlFor="wizard-email-col"
                                                className="cursor-pointer text-xs font-normal text-muted-foreground"
                                            >
                                                Also email the COL PDF to the student as an attachment
                                            </Label>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        )}

                        {/* Footer nav */}
                        <div className="flex items-center justify-between pt-5 mt-2 border-t border-border">
                            <Button
                                variant="ghost"
                                onClick={step === 0 ? () => onOpenChange(false) : goBack}
                                disabled={saving}
                                data-testid="wizard-back-btn"
                            >
                                {step === 0 ? (
                                    "Cancel"
                                ) : (
                                    <>
                                        <ChevronLeft className="h-4 w-4 mr-1" />
                                        Back
                                    </>
                                )}
                            </Button>
                            {step < STEPS.length - 1 ? (
                                <Button
                                    onClick={goNext}
                                    className="bg-primary hover:bg-primary/90 gap-1.5"
                                    data-testid="wizard-next-btn"
                                >
                                    Continue
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            ) : (
                                <Button
                                    onClick={submit}
                                    disabled={saving}
                                    className="bg-primary hover:bg-primary/90 gap-1.5"
                                    data-testid="wizard-submit-btn"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Enrolling…
                                        </>
                                    ) : (
                                        <>
                                            <ArrowRight className="h-4 w-4" />
                                            Confirm & enrol
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

function ReviewRow({ Icon, label, value, sub }) {
    return (
        <div className="flex items-start gap-3">
            <div className="mt-0.5 h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                    {label}
                </div>
                <div className="text-sm text-foreground font-medium truncate">{value}</div>
                {sub ? (
                    <div className="text-xs text-muted-foreground truncate">{sub}</div>
                ) : null}
            </div>
        </div>
    );
}

function SuccessPanel({ result, onCopy, onClose, setResult }) {
    const loa = result.loa && !result.loa.error ? result.loa : null;
    const loaError = result.loa?.error;
    const col = result.col && !result.col.error ? result.col : null;
    const colError = result.col?.error;
    const [issuingLoa, setIssuingLoa] = useState(false);
    const [issuingCol, setIssuingCol] = useState(false);

    const issueLoaNow = async () => {
        if (!result?.enrollment_id) return;
        setIssuingLoa(true);
        try {
            const { data } = await api.post(
                `/admin/enrollments/${result.enrollment_id}/loa`,
                { email_loa: true },
            );
            setResult((r) => ({
                ...r,
                loa: {
                    loa_number: data.loa.loa_number,
                    loa_id: data.loa.id,
                    download_url: data.download_url,
                    emailed_to: data.loa.emailed_to,
                },
            }));
            toast.success(
                data.loa.emailed_to
                    ? `LOA ${data.loa.loa_number} issued & emailed to ${data.loa.emailed_to}`
                    : `LOA ${data.loa.loa_number} issued.`,
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not issue LOA.");
        } finally {
            setIssuingLoa(false);
        }
    };

    const issueColNow = async () => {
        if (!result?.enrollment_id) return;
        setIssuingCol(true);
        try {
            const { data } = await api.post(
                `/admin/enrollments/${result.enrollment_id}/col`,
                { email_loa: true },
            );
            setResult((r) => ({
                ...r,
                col: {
                    col_number: data.loa.loa_number,
                    col_id: data.loa.id,
                    download_url: data.download_url,
                    emailed_to: data.loa.emailed_to,
                },
            }));
            toast.success(
                data.loa.emailed_to
                    ? `Conditional Offer ${data.loa.loa_number} issued & emailed to ${data.loa.emailed_to}`
                    : `Conditional Offer ${data.loa.loa_number} issued.`,
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not issue Conditional Offer Letter.");
        } finally {
            setIssuingCol(false);
        }
    };

    const downloadLoa = async (downloadUrl, loaNumber) => {
        try {
            const resp = await api.get(downloadUrl.replace(/^\/api/, ""), { responseType: "blob" });
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${loaNumber || "letter-of-acceptance"}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Could not download LOA.");
        }
    };

    return (
        <div
            className="rounded-lg border border-primary/30 bg-primary/5 p-5 space-y-4"
            data-testid="wizard-success-panel"
        >
            <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-primary mt-0.5" />
                <div className="flex-1">
                    <h3 className="font-display text-xl text-primary">
                        {result.user.created
                            ? "Student account created & enrolled"
                            : "Existing student enrolled"}
                    </h3>
                    <p className="text-sm text-foreground/85 mt-1">
                        <strong>{result.user.name}</strong> ({result.user.email}) is now enrolled in{" "}
                        <em>{result.program.title}</em>.
                    </p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                        <Badge
                            variant="secondary"
                            className="text-xs capitalize bg-accent/15 text-accent-foreground border-accent/30"
                        >
                            <Wallet className="h-3 w-3 mr-1" />
                            {result.payment_mode.replace(/_/g, " ")}
                        </Badge>
                        {result.email_sent ? (
                            <Badge
                                variant="outline"
                                className="text-xs border-primary/40 text-primary"
                            >
                                <Mail className="h-3 w-3 mr-1" />
                                Welcome email sent
                            </Badge>
                        ) : null}
                        {result.admissions_email_sent ? (
                            <Badge
                                variant="outline"
                                className="text-xs border-emerald-500/40 text-emerald-700"
                                data-testid="wizard-admissions-email-badge"
                            >
                                <Mail className="h-3 w-3 mr-1" />
                                admissions@hccbc.com notified
                            </Badge>
                        ) : null}
                        {loa ? (
                            <Badge
                                variant="outline"
                                className="text-xs border-amber-500/40 text-amber-700"
                            >
                                <IdCard className="h-3 w-3 mr-1" />
                                LOA · {loa.loa_number}
                            </Badge>
                        ) : null}
                        {col ? (
                            <Badge
                                variant="outline"
                                className="text-xs border-primary/40 text-primary"
                                data-testid="wizard-col-badge"
                            >
                                <IdCard className="h-3 w-3 mr-1" />
                                Conditional · {col.col_number}
                            </Badge>
                        ) : null}
                    </div>
                </div>
            </div>

            {result.temporary_password ? (
                <div className="rounded-md bg-background border border-border p-3">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 font-semibold">
                        Temporary password — share with student
                    </div>
                    <div className="flex items-center gap-2">
                        <code
                            className="flex-1 font-mono text-sm bg-muted/60 rounded px-3 py-1.5"
                            data-testid="wizard-temp-password"
                        >
                            {result.temporary_password}
                        </code>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onCopy(result.temporary_password)}
                            className="gap-1.5"
                            data-testid="wizard-copy-password-btn"
                        >
                            <Copy className="h-3.5 w-3.5" />
                            Copy
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                        Student can change this after first login.
                    </p>
                </div>
            ) : null}

            {/* LOA card */}
            <div className="rounded-md bg-background border border-border p-3">
                <div className="flex items-start gap-2">
                    <IdCard className="h-4 w-4 text-amber-700 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">Letter of Acceptance</div>
                        {loa ? (
                            <>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {loa.loa_number} — ready to download.
                                    {loa.emailed_to ? (
                                        <span className="block text-primary mt-0.5">
                                            Emailed to {loa.emailed_to}
                                        </span>
                                    ) : null}
                                </p>
                                <div className="mt-2 flex gap-2 flex-wrap">
                                    <Button
                                        size="sm"
                                        onClick={() => downloadLoa(loa.download_url, loa.loa_number)}
                                        className="bg-primary hover:bg-primary/90 gap-1.5"
                                        data-testid="wizard-loa-download-btn"
                                    >
                                        <IdCard className="h-3.5 w-3.5" />
                                        Download LOA PDF
                                    </Button>
                                    {!loa.emailed_to ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={issueLoaNow}
                                            disabled={issuingLoa}
                                            className="gap-1.5"
                                            data-testid="wizard-loa-resend-btn"
                                        >
                                            {issuingLoa ? (
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            ) : (
                                                <Mail className="h-3.5 w-3.5" />
                                            )}
                                            Email it to student
                                        </Button>
                                    ) : null}
                                </div>
                            </>
                        ) : loaError ? (
                            <>
                                <p className="text-xs text-amber-700 mt-0.5">{loaError}</p>
                                <p className="text-xs text-muted-foreground mt-1.5">
                                    Edit the student profile (DOB / address) and click below to issue.
                                </p>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={issueLoaNow}
                                    disabled={issuingLoa}
                                    className="gap-1.5 mt-2"
                                    data-testid="wizard-loa-retry-btn"
                                >
                                    {issuingLoa ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <IdCard className="h-3.5 w-3.5" />
                                    )}
                                    Try issuing LOA now
                                </Button>
                            </>
                        ) : (
                            <>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Not issued yet — generate now if the student needs proof of acceptance.
                                </p>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={issueLoaNow}
                                    disabled={issuingLoa}
                                    className="gap-1.5 mt-2"
                                    data-testid="wizard-loa-issue-btn"
                                >
                                    {issuingLoa ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <IdCard className="h-3.5 w-3.5" />
                                    )}
                                    Issue LOA now (and email it)
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* COL card — only render when COL was requested OR returned */}
            {(col || colError) ? (
                <div
                    className="rounded-md bg-background border border-primary/30 p-3"
                    data-testid="wizard-col-card"
                >
                    <div className="flex items-start gap-2">
                        <IdCard className="h-4 w-4 text-primary mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">Conditional Offer Letter</div>
                            {col ? (
                                <>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {col.col_number} — ready to download.
                                        {col.emailed_to ? (
                                            <span className="block text-primary mt-0.5">
                                                Emailed to {col.emailed_to}
                                            </span>
                                        ) : null}
                                    </p>
                                    <div className="mt-2 flex gap-2 flex-wrap">
                                        <Button
                                            size="sm"
                                            onClick={() => downloadLoa(col.download_url, col.col_number)}
                                            className="bg-primary hover:bg-primary/90 gap-1.5"
                                            data-testid="wizard-col-download-btn"
                                        >
                                            <IdCard className="h-3.5 w-3.5" />
                                            Download COL PDF
                                        </Button>
                                        {!col.emailed_to ? (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={issueColNow}
                                                disabled={issuingCol}
                                                className="gap-1.5"
                                                data-testid="wizard-col-resend-btn"
                                            >
                                                {issuingCol ? (
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                ) : (
                                                    <Mail className="h-3.5 w-3.5" />
                                                )}
                                                Email it to student
                                            </Button>
                                        ) : null}
                                    </div>
                                </>
                            ) : colError ? (
                                <>
                                    <p className="text-xs text-amber-700 mt-0.5">{colError}</p>
                                    <p className="text-xs text-muted-foreground mt-1.5">
                                        Edit the student profile (DOB / address / dates) and try again.
                                    </p>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={issueColNow}
                                        disabled={issuingCol}
                                        className="gap-1.5 mt-2"
                                        data-testid="wizard-col-retry-btn"
                                    >
                                        {issuingCol ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <IdCard className="h-3.5 w-3.5" />
                                        )}
                                        Try issuing COL now
                                    </Button>
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}

            <div className="flex justify-end pt-1">
                <Button
                    onClick={onClose}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="wizard-done-btn"
                >
                    Done
                </Button>
            </div>
        </div>
    );
}
